import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { HiArrowRight, HiLightBulb, HiOfficeBuilding, HiUserGroup } from 'react-icons/hi';

const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const services = [
    {
      icon: HiLightBulb,
      title: '1:1 Leadership Mentorship',
      description: 'Personalized support for leaders seeking clarity, confidence, and stronger decision-making.',
      outcomes: [
        'Clear direction',
        'Better leadership presence',
        'Improved decision-making',
      ],
      cta: 'Book a Call',
    },
    {
      icon: HiOfficeBuilding,
      title: 'Organizational Well-being Consulting',
      description: 'Strategic support for companies looking to improve culture, reduce burnout, and enhance performance.',
      outcomes: [
        'Healthier teams',
        'Better engagement',
        'Sustainable growth',
      ],
      cta: 'Learn More',
    },
    {
      icon: HiUserGroup,
      title: 'Workshops & Trainings',
      description: 'Interactive sessions designed to develop leadership skills and strengthen team dynamics.',
      outcomes: [
        'Stronger collaboration',
        'Increased awareness',
        'Practical tools',
      ],
      cta: 'Explore Workshops',
    },
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="services"
      ref={ref}
      data-testid="services-section"
      className="section-padding bg-[#F3EFEB]"
    >
      <div className="container-main">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="overline text-[#7A8B71] mb-4">Services</p>
          <h2 className="heading-2 text-[#0A192F]" data-testid="services-headline">
            How I Work
          </h2>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="card-service flex flex-col h-full"
              data-testid={`service-card-${index + 1}`}
            >
              {/* Icon */}
              <div className="w-14 h-14 rounded-full bg-[#7A8B71]/10 flex items-center justify-center mb-6">
                <service.icon className="w-7 h-7 text-[#7A8B71]" />
              </div>

              {/* Title */}
              <h3 className="heading-3 text-[#0A192F] mb-4">
                {service.title}
              </h3>

              {/* Description */}
              <p className="body-text text-[#4A5568] mb-6">
                {service.description}
              </p>

              {/* Outcomes */}
              <div className="mb-8 flex-grow">
                <p className="overline text-[#0A192F] mb-3">Outcomes:</p>
                <ul className="space-y-2">
                  {service.outcomes.map((outcome, i) => (
                    <li key={i} className="flex items-center gap-2 text-[#4A5568] body-text">
                      <span className="w-1.5 h-1.5 bg-[#7A8B71] rounded-full" />
                      {outcome}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <button
                onClick={scrollToContact}
                className="btn-secondary w-full justify-center"
                data-testid={`service-cta-${index + 1}`}
              >
                {service.cta}
                <HiArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
